import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Search, X } from "lucide-react";
import {
  useAssignEmployeeToRoleMutation,
  useAssignPrivilegeToRoleMutation,
  useGetAssignableEmployeesQuery,
  useGetAssignedRolePrivilegesQuery,
  useGetPrivilegeCatalogQuery,
  useRemoveEmployeeFromRoleMutation,
  useRemovePrivilegeFromRoleMutation,
} from "../store";
import { rolePrimaryLabel } from "../utils/roleDisplay";
import { getPageTitle } from "../utils/pageTitles";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ApiErrorText,
  apiCaughtError,
  extractLogMessage,
  isUnexpectedErrorMessage,
} from "../components/ApiErrorText";

const COMPONENT_PAGE_FILTER_ALL = "all";
const COMPONENT_PAGE_FILTER_NONE = "__none__";

function privilegeComponentPage(privilege) {
  return String(privilege?.componentPage ?? "").trim();
}

function formatComponentPageOptionLabel(page) {
  const path = String(page ?? "").trim();
  if (!path) {
    return "No component page";
  }
  const title = getPageTitle(path);
  if (title && title !== "Hibbert Accounting") {
    return `${title} (${path})`;
  }
  return path;
}

function formatPrivilegeOptionLabel(privilege) {
  const name = privilege?.privilegeName || `Privilege #${privilege?.privilegeId}`;
  const page = privilegeComponentPage(privilege);
  return page ? `${name} · ${page}` : name;
}

function formatLoadError(err, fallback) {
  if (!err) {
    return fallback;
  }
  if (err.status === 403) {
    return "You do not have permission to view this data.";
  }
  const data = err?.data;
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (err?.status === "FETCH_ERROR") {
    return "Network error. Check that the API server is running.";
  }
  if (typeof err?.status === "number") {
    return `${fallback} (HTTP ${err.status}).`;
  }
  return fallback;
}

function formatEmployeeLabel(employee) {
  const name = String(employee?.displayName ?? "").trim();
  if (name) {
    return name;
  }
  if (employee?.empId != null) {
    return `Employee #${employee.empId}`;
  }
  return "—";
}

function RolePrivilegesCollapsible({ roleKey, roleId, privileges, expanded, onToggle }) {
  const items = Array.isArray(privileges) ? privileges : [];
  const count = items.length;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="app-btn-plain w-full flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left text-gray-900 dark:text-gray-100"
        aria-expanded={expanded}
        aria-controls={`role-privileges-${roleKey}`}
      >
        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
          Assigned privileges ({count.toLocaleString()})
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
        )}
      </button>

      {expanded ? (
        <div id={`role-privileges-${roleKey}`} className="border-t border-gray-200 dark:border-gray-700">
          {count === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
              No privileges assigned to this role.
            </p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto">
              {items.map((privilege) => (
                <li
                  key={privilege.privilegeId ?? privilege.privilegeName}
                  className="px-3 py-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {privilege.privilegeName || "—"}
                      </div>
                      {privilege.privilegeDescription ? (
                        <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                          {privilege.privilegeDescription}
                        </div>
                      ) : null}
                      {privilege.componentPage ? (
                        <div className="mt-0.5 text-xs font-mono text-blue-600 dark:text-blue-400">
                          {privilege.componentPage}
                        </div>
                      ) : null}
                    </div>
                    <RemovePrivilegeButton roleId={roleId} privilege={privilege} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function RoleUsersCollapsible({ roleKey, roleId, employees, expanded, onToggle }) {
  const users = Array.isArray(employees) ? employees : [];
  const count = users.length;

  return (
    <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="app-btn-plain w-full flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left text-gray-900 dark:text-gray-100"
        aria-expanded={expanded}
        aria-controls={`role-users-${roleKey}`}
      >
        <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
          Users ({count.toLocaleString()})
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
        )}
      </button>

      {expanded ? (
        <div id={`role-users-${roleKey}`} className="border-t border-gray-200 dark:border-gray-700">
          {count === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
              No active users assigned to this role.
            </p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-64 overflow-y-auto">
              {users.map((employee) => (
                <li key={employee.empId ?? formatEmployeeLabel(employee)} className="px-3 py-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatEmployeeLabel(employee)}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                        Emp ID: {employee.empId ?? "—"}
                      </div>
                      {employee.emailAddress ? (
                        <div className="mt-0.5 text-xs text-blue-600 dark:text-blue-400 break-all">
                          {employee.emailAddress}
                        </div>
                      ) : null}
                    </div>
                    <RemoveEmployeeFromRoleButton roleId={roleId} employee={employee} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function formatAssignError(err) {
  const errors = err?.data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).find((value) => String(value ?? "").trim());
    if (first) {
      return String(first).trim();
    }
  }
  if (err?.status === 403) {
    return "You do not have permission to assign privileges.";
  }
  const detail = err?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (typeof err?.status === "number") {
    return `Unable to assign privilege (HTTP ${err.status}).`;
  }
  return "Unable to update role privileges.";
}

function RemoveEmployeeFromRoleButton({ roleId, employee }) {
  const [removeEmployeeFromRole, { isLoading }] = useRemoveEmployeeFromRoleMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const empId = employee?.empId;
  const employeeLabel = formatEmployeeLabel(employee);

  const handleOpenConfirm = () => {
    setError("");
    setConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    setError("");
    if (roleId == null || empId == null) {
      setError("Unable to remove user from role.");
      return;
    }

    try {
      await removeEmployeeFromRole({ roleId, empId }).unwrap();
      setConfirmOpen(false);
    } catch (err) {
      setError(apiCaughtError(err, formatAssignError));
    }
  };

  if (roleId == null || empId == null) {
    return null;
  }

  return (
    <div className="shrink-0">
      <button
        type="button"
        onClick={handleOpenConfirm}
        disabled={isLoading}
        aria-label={`Remove ${employeeLabel} from role`}
        className="app-btn-plain inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X className="w-3.5 h-3.5" />
        Remove
      </button>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!isLoading) {
            setConfirmOpen(open);
            if (!open) {
              setError("");
            }
          }
        }}
      >
        <AlertDialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user from role?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-400">
              Remove{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">{employeeLabel}</span> from
              this role? They will lose role privileges after they sign in again.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <ApiErrorText error={error} className="text-sm text-red-600 dark:text-red-400" />

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              onClick={handleConfirmRemove}
              disabled={isLoading}
              className="app-btn-gloss disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? "Removing…" : "Remove user"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RemovePrivilegeButton({ roleId, privilege }) {
  const [removePrivilegeFromRole, { isLoading }] = useRemovePrivilegeFromRoleMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const privilegeId = privilege?.privilegeId;
  const privilegeName = privilege?.privilegeName || "this privilege";

  const handleOpenConfirm = () => {
    setError("");
    setConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    setError("");
    if (roleId == null || privilegeId == null) {
      setError("Unable to remove privilege.");
      return;
    }

    try {
      await removePrivilegeFromRole({ roleId, privilegeId }).unwrap();
      setConfirmOpen(false);
    } catch (err) {
      setError(apiCaughtError(err, formatAssignError));
    }
  };

  if (roleId == null || privilegeId == null) {
    return null;
  }

  return (
    <div className="shrink-0">
      <button
        type="button"
        onClick={handleOpenConfirm}
        disabled={isLoading}
        aria-label={`Remove ${privilegeName}`}
        className="app-btn-plain inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X className="w-3.5 h-3.5" />
        Remove
      </button>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!isLoading) {
            setConfirmOpen(open);
            if (!open) {
              setError("");
            }
          }
        }}
      >
        <AlertDialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove privilege?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-400">
              Remove{" "}
              <span className="font-mono font-medium text-gray-900 dark:text-gray-100">
                {privilegeName}
              </span>{" "}
              from this role? Users with this role will lose the privilege after they sign in again.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <ApiErrorText error={error} className="text-sm text-red-600 dark:text-red-400" />

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              onClick={handleConfirmRemove}
              disabled={isLoading}
              className="app-btn-gloss disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? "Removing…" : "Remove privilege"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function employeeFirstName(employee) {
  const direct = String(employee?.firstName ?? "").trim();
  if (direct) {
    return direct;
  }
  const display = String(employee?.displayName ?? "").trim();
  if (!display) {
    return "";
  }
  const space = display.indexOf(" ");
  return space === -1 ? display : display.slice(0, space);
}

function employeeLastName(employee) {
  const direct = String(employee?.lastName ?? "").trim();
  if (direct) {
    return direct;
  }
  const display = String(employee?.displayName ?? "").trim();
  if (!display) {
    return "";
  }
  const space = display.indexOf(" ");
  return space === -1 ? "" : display.slice(space + 1).trim();
}

function matchesEmployeeSearch(employee, firstNameQuery, lastNameQuery, empIdQuery) {
  const first = firstNameQuery.trim().toLowerCase();
  const last = lastNameQuery.trim().toLowerCase();
  const empId = empIdQuery.trim();

  if (!first && !last && !empId) {
    return false;
  }
  if (first && !employeeFirstName(employee).toLowerCase().includes(first)) {
    return false;
  }
  if (last && !employeeLastName(employee).toLowerCase().includes(last)) {
    return false;
  }
  if (empId && !String(employee?.empId ?? "").includes(empId)) {
    return false;
  }
  return true;
}

function AddUserToRole({ roleId, roleName, roleDescription, assignedEmployees, employeeRoleByEmpId, onUserAssigned }) {
  const { data: assignableEmployees = [], isLoading: catalogLoading } = useGetAssignableEmployeesQuery();
  const [assignEmployeeToRole, { isLoading: isAssigning }] = useAssignEmployeeToRoleMutation();

  const [expanded, setExpanded] = useState(false);
  const [firstNameSearch, setFirstNameSearch] = useState("");
  const [lastNameSearch, setLastNameSearch] = useState("");
  const [empIdSearch, setEmpIdSearch] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingReassign, setPendingReassign] = useState(null);

  const targetRoleLabel = rolePrimaryLabel(roleDescription, roleName) || "this role";

  const assignedEmpIds = useMemo(() => {
    const ids = new Set();
    for (const employee of assignedEmployees ?? []) {
      if (employee?.empId != null) {
        ids.add(employee.empId);
      }
    }
    return ids;
  }, [assignedEmployees]);

  const availableEmployees = useMemo(() => {
    const catalog = Array.isArray(assignableEmployees) ? assignableEmployees : [];
    return catalog.filter((employee) => employee?.empId != null && !assignedEmpIds.has(employee.empId));
  }, [assignableEmployees, assignedEmpIds]);

  const hasSearchCriteria =
    firstNameSearch.trim().length > 0 ||
    lastNameSearch.trim().length > 0 ||
    empIdSearch.trim().length > 0;

  const searchResults = useMemo(() => {
    if (!hasSearchCriteria) {
      return [];
    }
    return availableEmployees.filter((employee) =>
      matchesEmployeeSearch(employee, firstNameSearch, lastNameSearch, empIdSearch),
    );
  }, [availableEmployees, firstNameSearch, lastNameSearch, empIdSearch, hasSearchCriteria]);

  const resetPanel = () => {
    setExpanded(false);
    setFirstNameSearch("");
    setLastNameSearch("");
    setEmpIdSearch("");
    setError("");
    setPendingReassign(null);
    setConfirmOpen(false);
  };

  const clearSearch = () => {
    setFirstNameSearch("");
    setLastNameSearch("");
    setEmpIdSearch("");
    setError("");
    setSuccessMessage("");
  };

  const resolveCurrentRole = (empId, employee) => {
    const fromList = employeeRoleByEmpId?.get(empId);
    if (fromList) {
      return fromList;
    }
    if (employee?.currentRoleId != null) {
      return {
        roleId: employee.currentRoleId,
        roleName: employee.currentRoleName,
        roleDescription: employee.currentRoleDescription,
        roleLabel: rolePrimaryLabel(employee.currentRoleDescription, employee.currentRoleName),
      };
    }
    return null;
  };

  const performAssign = async (empId) => {
    setError("");
    setSuccessMessage("");

    if (roleId == null) {
      setError("Role is missing.");
      return;
    }

    try {
      const result = await assignEmployeeToRole({ roleId, empId }).unwrap();
      const label = formatEmployeeLabel(result.employee) || "User";
      setSuccessMessage(`${label} assigned.`);
      setConfirmOpen(false);
      setPendingReassign(null);
      onUserAssigned?.();
    } catch (err) {
      setError(apiCaughtError(err, formatAssignError));
    }
  };

  const handleAssignEmployee = (employee) => {
    setError("");
    setSuccessMessage("");

    const empId = employee?.empId;
    if (empId == null) {
      setError("Unable to assign user.");
      return;
    }
    if (roleId == null) {
      setError("Role is missing.");
      return;
    }

    const currentRole = resolveCurrentRole(empId, employee);
    const isReassign = currentRole != null && currentRole.roleId !== roleId;

    if (isReassign) {
      setPendingReassign({
        empId,
        employeeLabel: formatEmployeeLabel(employee),
        fromRoleLabel: currentRole.roleLabel || currentRole.roleName || "another role",
        fromRoleName: currentRole.roleName,
      });
      setConfirmOpen(true);
      return;
    }

    performAssign(empId);
  };

  const handleConfirmReassign = () => {
    if (pendingReassign?.empId != null) {
      performAssign(pendingReassign.empId);
    }
  };

  if (roleId == null) {
    return null;
  }

  return (
    <>
      <div className="mt-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => {
            if (expanded) {
              resetPanel();
            } else {
              setExpanded(true);
              setError("");
              setSuccessMessage("");
            }
          }}
          disabled={isAssigning}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50 ${
            expanded
              ? "app-btn-plain font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
              : "app-btn-gloss"
          }`}
        >
          {expanded ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <Plus className="w-3.5 h-3.5" />
          )}
          {expanded ? "Close" : "Add user"}
        </button>

        {expanded ? (
          <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/60 space-y-3">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Search by first name, last name, and/or employee ID. Results can be assigned to{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">{targetRoleLabel}</span>.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label
                  htmlFor={`add-user-first-${roleId}`}
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  First name
                </label>
                <input
                  id={`add-user-first-${roleId}`}
                  type="text"
                  value={firstNameSearch}
                  onChange={(e) => {
                    setFirstNameSearch(e.target.value);
                    setError("");
                    setSuccessMessage("");
                  }}
                  disabled={catalogLoading || isAssigning}
                  placeholder="Search first name…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                />
              </div>
              <div>
                <label
                  htmlFor={`add-user-last-${roleId}`}
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Last name
                </label>
                <input
                  id={`add-user-last-${roleId}`}
                  type="text"
                  value={lastNameSearch}
                  onChange={(e) => {
                    setLastNameSearch(e.target.value);
                    setError("");
                    setSuccessMessage("");
                  }}
                  disabled={catalogLoading || isAssigning}
                  placeholder="Search last name…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                />
              </div>
              <div>
                <label
                  htmlFor={`add-user-emp-${roleId}`}
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Emp ID
                </label>
                <input
                  id={`add-user-emp-${roleId}`}
                  type="text"
                  inputMode="numeric"
                  value={empIdSearch}
                  onChange={(e) => {
                    setEmpIdSearch(e.target.value);
                    setError("");
                    setSuccessMessage("");
                  }}
                  disabled={catalogLoading || isAssigning}
                  placeholder="Search emp ID…"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                />
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                  {catalogLoading
                    ? "Loading users…"
                    : hasSearchCriteria
                      ? `Results (${searchResults.length.toLocaleString()})`
                      : "Results"}
                </span>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
                {catalogLoading ? (
                  <p className="px-3 py-4 text-xs text-gray-500 dark:text-gray-400 text-center">Loading…</p>
                ) : !hasSearchCriteria ? (
                  <p className="px-3 py-4 text-xs text-gray-500 dark:text-gray-400 text-center">
                    Enter at least one search field to find users.
                  </p>
                ) : searchResults.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-gray-500 dark:text-gray-400 text-center">
                    No users match your search.
                  </p>
                ) : (
                  searchResults.map((employee) => {
                    const currentRole = resolveCurrentRole(employee.empId, employee);
                    const currentLabel =
                      currentRole?.roleLabel ||
                      rolePrimaryLabel(currentRole?.roleDescription, currentRole?.roleName) ||
                      currentRole?.roleName;
                    return (
                      <div
                        key={employee.empId}
                        className="px-3 py-2 flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {formatEmployeeLabel(employee)}
                          </div>
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                            Emp ID: {employee.empId ?? "—"}
                          </div>
                          {employee.emailAddress ? (
                            <div className="mt-0.5 text-xs text-blue-600 dark:text-blue-400 break-all">
                              {employee.emailAddress}
                            </div>
                          ) : null}
                          {currentLabel ? (
                            <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
                              Currently: {currentLabel}
                            </div>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAssignEmployee(employee)}
                          disabled={isAssigning}
                          className="app-btn-plain shrink-0 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded transition-colors disabled:opacity-50"
                        >
                          Assign
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <ApiErrorText error={error} className="text-xs text-red-600 dark:text-red-400" />
            {successMessage ? (
              <p className="text-xs text-green-700 dark:text-green-300">{successMessage}</p>
            ) : null}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearSearch}
                disabled={isAssigning || !hasSearchCriteria}
                className="app-btn-plain px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear search
              </button>
            </div>
          </div>
        ) : null}

        {successMessage && !expanded ? (
          <p className="px-3 py-2 text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/30 border-t border-green-200 dark:border-green-900">
            {successMessage}
          </p>
        ) : null}
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!isAssigning) {
            setConfirmOpen(open);
            if (!open) {
              setPendingReassign(null);
              setError("");
            }
          }
        }}
      >
        <AlertDialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Change user&apos;s role?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {pendingReassign?.employeeLabel ?? "This user"}
              </span>{" "}
              is currently assigned to{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {pendingReassign?.fromRoleLabel ?? "another role"}
              </span>
              {pendingReassign?.fromRoleName ? (
                <>
                  {" "}
                  (
                  <span className="font-mono">{pendingReassign.fromRoleName}</span>)
                </>
              ) : null}
              . Assigning them to{" "}
              <span className="font-medium text-gray-900 dark:text-gray-100">{targetRoleLabel}</span>
              {roleName ? (
                <>
                  {" "}
                  (<span className="font-mono">{roleName}</span>)
                </>
              ) : null}{" "}
              will remove them from their current role. They will need to sign in again for privilege changes to
              apply.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {confirmOpen ? (
            <ApiErrorText error={error} className="text-sm text-red-600 dark:text-red-400" />
          ) : null}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isAssigning}>
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              onClick={handleConfirmReassign}
              disabled={isAssigning}
              className="app-btn-gloss disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isAssigning ? "Assigning…" : "Confirm change"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function AddPrivilegeToRole({ roleId, assignedPrivileges, onPrivilegeAssigned }) {
  const { data: privilegeCatalog = [], isLoading: catalogLoading } = useGetPrivilegeCatalogQuery();
  const [assignPrivilegeToRole, { isLoading: isAssigning }] = useAssignPrivilegeToRoleMutation();

  const [expanded, setExpanded] = useState(false);
  const [selectedPrivilegeId, setSelectedPrivilegeId] = useState("");
  const [filterComponentPage, setFilterComponentPage] = useState(COMPONENT_PAGE_FILTER_ALL);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const assignedIds = useMemo(() => {
    const ids = new Set();
    for (const privilege of assignedPrivileges ?? []) {
      if (privilege?.privilegeId != null) {
        ids.add(privilege.privilegeId);
      }
    }
    return ids;
  }, [assignedPrivileges]);

  const availablePrivileges = useMemo(() => {
    const catalog = Array.isArray(privilegeCatalog) ? privilegeCatalog : [];
    return catalog.filter((privilege) => privilege?.privilegeId != null && !assignedIds.has(privilege.privilegeId));
  }, [privilegeCatalog, assignedIds]);

  const componentPageOptions = useMemo(() => {
    const pages = new Set();
    let hasNone = false;
    for (const privilege of availablePrivileges) {
      const page = privilegeComponentPage(privilege);
      if (!page) {
        hasNone = true;
      } else {
        pages.add(page);
      }
    }
    return {
      pages: Array.from(pages).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
      hasNone,
    };
  }, [availablePrivileges]);

  const filteredPrivileges = useMemo(() => {
    if (filterComponentPage === COMPONENT_PAGE_FILTER_ALL) {
      return availablePrivileges;
    }
    return availablePrivileges.filter((privilege) => {
      const page = privilegeComponentPage(privilege);
      if (filterComponentPage === COMPONENT_PAGE_FILTER_NONE) {
        return !page;
      }
      return page === filterComponentPage;
    });
  }, [availablePrivileges, filterComponentPage]);

  const resetPanel = () => {
    setExpanded(false);
    setSelectedPrivilegeId("");
    setFilterComponentPage(COMPONENT_PAGE_FILTER_ALL);
    setError("");
  };

  const handleAssign = async () => {
    setError("");
    setSuccessMessage("");

    const privilegeId = Number.parseInt(selectedPrivilegeId, 10);
    if (!Number.isFinite(privilegeId) || privilegeId <= 0) {
      setError("Select a privilege to assign.");
      return;
    }
    if (roleId == null) {
      setError("Role is missing.");
      return;
    }

    try {
      const result = await assignPrivilegeToRole({ roleId, privilegeId }).unwrap();
      setSuccessMessage(`${result.privilege?.privilegeName ?? "Privilege"} assigned.`);
      setSelectedPrivilegeId("");
      setFilterComponentPage(COMPONENT_PAGE_FILTER_ALL);
      setExpanded(false);
      onPrivilegeAssigned?.();
    } catch (err) {
      setError(apiCaughtError(err, formatAssignError));
    }
  };

  if (roleId == null) {
    return null;
  }

  return (
    <div className="mt-3 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
      {!expanded ? (
        <button
          type="button"
          onClick={() => {
            setExpanded(true);
            setError("");
            setSuccessMessage("");
          }}
          className="app-btn-gloss w-full flex items-center justify-center gap-2 px-3 py-2 text-xs"
        >
          <Plus className="w-3.5 h-3.5" />
          Add privilege
        </button>
      ) : (
        <div className="p-3 bg-gray-50 dark:bg-gray-800/60 space-y-3">
          <div>
            <label
              htmlFor={`add-privilege-page-${roleId}`}
              className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Component page
            </label>
            <select
              id={`add-privilege-page-${roleId}`}
              value={filterComponentPage}
              onChange={(e) => {
                const nextFilter = e.target.value;
                setFilterComponentPage(nextFilter);
                setError("");
                setSuccessMessage("");
                const selected = availablePrivileges.find(
                  (privilege) => String(privilege.privilegeId) === selectedPrivilegeId,
                );
                if (!selected) {
                  setSelectedPrivilegeId("");
                  return;
                }
                const page = privilegeComponentPage(selected);
                const stillVisible =
                  nextFilter === COMPONENT_PAGE_FILTER_ALL ||
                  (nextFilter === COMPONENT_PAGE_FILTER_NONE ? !page : page === nextFilter);
                if (!stillVisible) {
                  setSelectedPrivilegeId("");
                }
              }}
              disabled={catalogLoading || isAssigning || availablePrivileges.length === 0}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
            >
              <option value={COMPONENT_PAGE_FILTER_ALL}>All component pages</option>
              {componentPageOptions.hasNone ? (
                <option value={COMPONENT_PAGE_FILTER_NONE}>No component page</option>
              ) : null}
              {componentPageOptions.pages.map((page) => (
                <option key={page} value={page}>
                  {formatComponentPageOptionLabel(page)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor={`add-privilege-${roleId}`}
              className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Privilege to assign
            </label>
            <select
              id={`add-privilege-${roleId}`}
              value={selectedPrivilegeId}
              onChange={(e) => {
                setSelectedPrivilegeId(e.target.value);
                setError("");
                setSuccessMessage("");
              }}
              disabled={catalogLoading || isAssigning || filteredPrivileges.length === 0}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
            >
              <option value="">
                {catalogLoading
                  ? "Loading privileges…"
                  : availablePrivileges.length === 0
                    ? "No unassigned privileges available"
                    : filteredPrivileges.length === 0
                      ? "No privileges match this component page"
                      : "Select a privilege"}
              </option>
              {filteredPrivileges.map((privilege) => (
                <option key={privilege.privilegeId} value={privilege.privilegeId}>
                  {formatPrivilegeOptionLabel(privilege)}
                </option>
              ))}
            </select>
          </div>

          <ApiErrorText error={error} className="text-xs text-red-600 dark:text-red-400" />

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAssign}
              disabled={catalogLoading || isAssigning || filteredPrivileges.length === 0}
              className="app-btn-plain px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAssigning ? "Assigning…" : "Assign"}
            </button>
            <button
              type="button"
              onClick={resetPanel}
              disabled={isAssigning}
              className="app-btn-plain px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {successMessage && !expanded ? (
        <p className="px-3 py-2 text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/30 border-t border-green-200 dark:border-green-900">
          {successMessage}
        </p>
      ) : null}
    </div>
  );
}

function AssignedRolePrivileges() {
  const { data, isLoading, isError, error, refetch } = useGetAssignedRolePrivilegesQuery();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRoleName, setFilterRoleName] = useState("all");
  const [expandedRoleKeys, setExpandedRoleKeys] = useState(() => new Set());
  const [expandedPrivilegeRoleKeys, setExpandedPrivilegeRoleKeys] = useState(() => new Set());

  const roles = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const employeeRoleByEmpId = useMemo(() => {
    const map = new Map();
    for (const role of roles) {
      if (role.roleId == null) {
        continue;
      }
      const roleLabel = rolePrimaryLabel(role.roleDescription, role.roleName);
      for (const employee of role.employees ?? []) {
        if (employee?.empId != null && !map.has(employee.empId)) {
          map.set(employee.empId, {
            roleId: role.roleId,
            roleName: role.roleName,
            roleDescription: role.roleDescription,
            roleLabel,
          });
        }
      }
    }
    return map;
  }, [roles]);

  const roleFilterOptions = useMemo(() => {
    const byName = new Map();
    for (const role of roles) {
      const roleName = String(role.roleName ?? "").trim();
      if (!roleName || byName.has(roleName)) {
        continue;
      }
      byName.set(roleName, {
        roleName,
        label: roleName,
      });
    }
    return Array.from(byName.values()).sort((a, b) =>
      a.roleName.localeCompare(b.roleName, undefined, { sensitivity: "base" }),
    );
  }, [roles]);

  const q = searchTerm.trim().toLowerCase();
  const filteredRoles = useMemo(() => {
    return roles.filter((role) => {
      const roleName = String(role.roleName ?? "").trim();
      if (filterRoleName !== "all" && roleName !== filterRoleName) {
        return false;
      }
      if (!q) {
        return true;
      }
      const roleHay = [role.roleName, role.roleDescription]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      const privilegeHay = (role.privileges ?? [])
        .flatMap((p) => [p.privilegeName, p.privilegeDescription, p.componentPage])
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      const employeeHay = (role.employees ?? [])
        .flatMap((e) => [e.displayName, e.emailAddress, e.empId])
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return `${roleHay} ${privilegeHay} ${employeeHay}`.includes(q);
    });
  }, [roles, q, filterRoleName]);

  const hasActiveFilters = filterRoleName !== "all" || q.length > 0;

  const toggleRoleUsers = (roleKey) => {
    setExpandedRoleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(roleKey)) {
        next.delete(roleKey);
      } else {
        next.add(roleKey);
      }
      return next;
    });
  };

  const toggleRolePrivileges = (roleKey) => {
    setExpandedPrivilegeRoleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(roleKey)) {
        next.delete(roleKey);
      } else {
        next.add(roleKey);
      }
      return next;
    });
  };

  const loadErrorMessage = formatLoadError(error, "Unable to load assigned role privileges.");

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Assigned Role Privileges
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Billing roles and the privileges granted to each role
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[16rem] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search roles or privileges…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isLoading || isError}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          />
        </div>
        <select
          value={filterRoleName}
          onChange={(e) => setFilterRoleName(e.target.value)}
          disabled={isLoading || isError || roleFilterOptions.length === 0}
          aria-label="Filter by role name"
          className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
        >
          <option value="all">All Roles</option>
          {roleFilterOptions.map(({ roleName, label }) => (
            <option key={roleName} value={roleName}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {isError ? (
        <div className="rounded border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-800 dark:text-red-200 mb-6">
          <p className="font-medium">Could not load role privileges</p>
          <ApiErrorText
            error={
              isUnexpectedErrorMessage(loadErrorMessage)
                ? { message: loadErrorMessage, logMessage: extractLogMessage(error) }
                : loadErrorMessage
            }
            className="mt-1 block text-red-800 dark:text-red-200"
          />
          <button
            type="button"
            onClick={() => refetch()}
            className="app-btn-gloss mt-3 px-3 py-1.5 text-sm"
          >
            Retry
          </button>
        </div>
      ) : null}

      {!isError ? (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Privileges
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {isLoading ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                      Loading…
                    </td>
                  </tr>
                ) : filteredRoles.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
                      {hasActiveFilters ? "No roles match your filters." : "No roles found."}
                    </td>
                  </tr>
                ) : (
                  filteredRoles.map((role) => {
                    const privileges = Array.isArray(role.privileges) ? role.privileges : [];
                    const employees = Array.isArray(role.employees) ? role.employees : [];
                    const roleName = String(role.roleName ?? "").trim();
                    const roleKey = String(role.roleId ?? (roleName || "—"));
                    const usersExpanded = expandedRoleKeys.has(roleKey);
                    const privilegesExpanded = expandedPrivilegeRoleKeys.has(roleKey);
                    return (
                      <tr key={roleKey} className="hover:bg-gray-50 dark:hover:bg-gray-800 align-top">
                        <td className="px-6 py-4 text-sm">
                          <div className="font-bold text-gray-900 dark:text-gray-100 font-mono">
                            {roleName || "—"}
                          </div>
                          <RoleUsersCollapsible
                            roleKey={roleKey}
                            roleId={role.roleId}
                            employees={employees}
                            expanded={usersExpanded}
                            onToggle={() => toggleRoleUsers(roleKey)}
                          />
                          <AddUserToRole
                            roleId={role.roleId}
                            roleName={role.roleName}
                            roleDescription={role.roleDescription}
                            assignedEmployees={employees}
                            employeeRoleByEmpId={employeeRoleByEmpId}
                            onUserAssigned={() => {
                              setExpandedRoleKeys((prev) => {
                                const next = new Set(prev);
                                next.add(roleKey);
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {role.roleDescription || "—"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          <RolePrivilegesCollapsible
                            roleKey={roleKey}
                            roleId={role.roleId}
                            privileges={privileges}
                            expanded={privilegesExpanded}
                            onToggle={() => toggleRolePrivileges(roleKey)}
                          />
                          <AddPrivilegeToRole
                            roleId={role.roleId}
                            assignedPrivileges={privileges}
                            onPrivilegeAssigned={() => {
                              setExpandedPrivilegeRoleKeys((prev) => {
                                const next = new Set(prev);
                                next.add(roleKey);
                                return next;
                              });
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { AssignedRolePrivileges };
